import { formatFileSize } from "@circle-react/helpers/fileSizeHelpers";
import { Icon } from "@circleco/compass/components/Icon";
import type { IconName } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

export interface EditorBlockFile {
  byte_size?: number;
  content_type: string;
  filename: string;
  url: string;
}

const fileIconName = (contentType: string): IconName => {
  if (contentType === "application/pdf") return "file-pdf";
  if (contentType?.startsWith("audio")) return "sound-wave";
  if (contentType?.startsWith("video")) return "video";
  return "file";
};

export interface FileCardProps {
  className?: string;
  file: EditorBlockFile;
  isDownloadable?: boolean;
  onPreview?: () => void;
  previewUrl?: string;
}

export const FileCard = ({
  className,
  file,
  isDownloadable = true,
  onPreview,
  previewUrl,
}: FileCardProps) => (
  <div
    data-testid="editor-content-blocks-file-card"
    className={classNames(
      "bg-primary border-primary group/file-card relative w-full max-w-[500px] rounded-xl border px-3 py-2",
      className,
    )}
  >
    <div className="flex items-center gap-3">
      <Icon name={fileIconName(file.content_type)} size="lg" />
      <div className="flex min-w-0 flex-1 flex-col">
        <Typography variant="label-md" className="block truncate">
          {file.filename}
        </Typography>
        {Boolean(file.byte_size) && (
          <Typography variant="caption" color="tertiary" className="block">
            {formatFileSize(file.byte_size ?? 0)}
          </Typography>
        )}
      </div>
      {isDownloadable && (
        <span className="opacity-0 transition-opacity focus-within:opacity-100 group-hover/file-card:opacity-100">
          <IconButton
            icon="arrow-box-down"
            size="sm"
            variant="ghost"
            aria-label={t("editor_content_blocks.file_card.download")}
            onClick={event => event.stopPropagation()}
            renderLink={
              <a
                href={file.url}
                download
                target="_blank"
                rel="noreferrer"
                aria-label={t("editor_content_blocks.file_card.download")}
                className="!text-primary hover:!text-primary"
              />
            }
          />
        </span>
      )}
    </div>
    {previewUrl && (
      <div className="relative mt-2 flex max-h-[500px] items-center justify-center overflow-hidden rounded-lg">
        {onPreview && (
          <button
            type="button"
            onClick={onPreview}
            className="absolute left-0 top-0 size-full cursor-zoom-in"
            aria-label={t("editor_content_blocks.file_card.view_preview")}
          />
        )}
        <img
          loading="lazy"
          className="border-primary h-48 w-full rounded-lg border object-cover object-top"
          src={previewUrl}
          alt={t("editor_content_blocks.file_card.preview")}
        />
      </div>
    )}
  </div>
);
